'use client';

import { useState } from 'react';
import type { ReactNode } from 'react';

import { useDebugMode } from '@packages/shared/src/hooks/useDebugMode/useDebugMode';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { DialogFooter } from '@packages/shared/src/ui/Dialog/DialogFooter/DialogFooter';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { getAllTestParticipations } from '@packages/shared/src/abtesting/useAbTesting/getAllTestParticipations/getAllTestParticipations';
import { DialogHeader } from '../Dialog/DialogHeader/DialogHeader.client';

import { TestOverview } from './DebugPanel.TestOverview.client';

export type DebugPanelProps = {
  children?: ReactNode;
  addons?: ReactNode;
};

export const DebugPanel = ({ children, addons }: DebugPanelProps) => {
  const { debugModeActive, disableDebugMode } = useDebugMode();
  const [currentDialog, setCurrentDialog] = useState<null | 'tests' | 'info'>(null);

  const { getCookies } = useCookies();

  const activeTests = getAllTestParticipations(getCookies());

  const title = {
    tests: 'Active tests',
    info: 'Debug Info',
  }[currentDialog ?? 'info'];

  const closeDialog = () => setCurrentDialog(null);

  if (!debugModeActive) return null;

  return (
    <div className="z-app-bar border-color-warning bg-color-warning-light fixed bottom-0 left-0 m-2 flex flex-col items-start gap-1 rounded-sm border p-1">
      {activeTests.length > 0 && (
        <Button onClick={() => setCurrentDialog('tests')}>Active Tests</Button>
      )}

      <div className="flex items-center gap-1">
        <Button onClick={() => setCurrentDialog('info')}>Debug Info</Button>

        {addons}
      </div>

      <Dialog isOpen={!!currentDialog} onClose={closeDialog} className="w-full md:h-3/4">
        <DialogHeader onClose={closeDialog}>{title}</DialogHeader>
        <DialogContent className="flex flex-col overflow-y-auto">
          {currentDialog === 'tests' && <TestOverview />}
          {currentDialog === 'info' && children}
        </DialogContent>
        <DialogFooter>
          <Button
            onClick={() => {
              closeDialog();
              disableDebugMode();
            }}
          >
            Disable debug mode
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
};

// eslint-disable-next-line import-x/no-default-export
export default DebugPanel;
